import {
  Band,
  brand,
  Cta,
  Do,
  Eyebrow,
  Never,
  Page,
  PanelBar,
  Reveal,
  Rule,
  Runner,
  Stat,
  ui,
  VersionStrip,
} from '../ui';
import { REFUND_DAYS, REFUND_PLAYTIME_HOURS } from '@goodbuild/rules/refund-policy';
import { column } from './shared';
import { useDocumentTitle } from '../title';

/**
 * Who we are.
 *
 * **The page used to be about the shop and is about the studio now.** It
 * opened `Loud about real things` over a paragraph comparing us to other
 * stores, then spent four sections explaining a store page — a fine argument,
 * and the wrong one to lead with. Somebody who has just read *we make games*
 * wants to know who made them before they want the theory of a gallery.
 *
 * **The four store-page claims are gone, because none of them was true any
 * more.** They sold screenshots players voted on, a summary generated from
 * reviews, playing time, and every version's score. `routes/game.tsx` records
 * the removal of the first two, and neither playtime nor a score reaches any
 * HTTP route. What replaces them is what buying here is, which is checkable on
 * the store page today. The refund figures come from
 * `@goodbuild/rules/refund-policy` for the reason the legal documents give.
 *
 * **Short words, and about half the sentences.** Section 08's test did the
 * cutting again — *if a sentence survives having its adjectives removed it was
 * a fact, and if it collapses it was marketing* — and a second pass swapped the
 * long word wherever a short one said the same thing: `curated` for `chosen`,
 * `denominator` for `the count`, `attribution` for who gets paid. None of the
 * facts moved.
 *
 * The sections arrive as they are scrolled to rather than all at once, which is
 * `Reveal` and is the device the creators pitch has always used. Nothing on
 * this page is a measurement, so nothing that moves here is a figure moving —
 * see the note on `useTilt` in `store.tsx` for where that line is drawn.
 *
 * The version strip is drawn from a labelled illustration rather than from a
 * title, and says so. Section 03's one rule is that a strip must never show
 * data it does not have; explaining the device is legitimate, and captioning it
 * as an example is what keeps it from reading as a real score.
 */

/** An illustration of the device, captioned as one. Not a real title. */
const EXAMPLE_HISTORY = [
  { version: 'v1.0', positivePercent: 41, total: 96 },
  { version: 'v1.4', positivePercent: 48, total: 210 },
  { version: 'v2.0', positivePercent: 58, total: 340 },
  { version: 'v2.3', positivePercent: 74, total: 512 },
  { version: 'v2.6', positivePercent: 86, total: 74 },
];

export function AboutRoute(): React.ReactElement {
  useDocumentTitle('About us');

  return (
    <Page site="store">
      <Runner left="01 / Who we are" />

      <header style={column(6)}>
        <Eyebrow>About us</Eyebrow>
        <h1 className="gb-display">
          We make
          <br />
          games
        </h1>
        <p className="gb-lead">A small team. We make our own games and sell them right here.</p>
      </header>

      <PanelBar />

      <Reveal as="section" className={brand.g2}>
        <div style={column(3)}>
          <h2 className="gb-subhead">Who we are</h2>
          <p className="gb-body">A tiny studio. A few of us, one game at a time.</p>
          <p className="gb-body">
            We made a game once and lost money on everything that came after it. So we built this
            instead.
          </p>
        </div>
        <div style={column(3)}>
          <h2 className="gb-subhead">What is here</h2>
          <p className="gb-body">Our games. Nothing on this site was made by anyone else.</p>
          <p className="gb-body">
            Buy one and it is yours to keep. We never take a game back to punish anybody.
          </p>
        </div>
      </Reveal>

      <Rule />

      {/*
        Wiped rather than risen, which is the one heading on this page worth
        spending the louder arrival on. Nothing in here takes focus and nothing
        in here draws outside its own box, which is what the variant requires —
        see `Reveal`.
      */}
      <Reveal as="section" variant="wipe" className={ui.stack}>
        <div style={column(4)}>
          <Eyebrow>Buying here</Eyebrow>
          <h2 className="gb-headline">
            Yours
            <br />
            to keep
          </h2>
          <p className="gb-lead">
            One price, one payment, and a refund button you do not have to ask anybody for.
          </p>
        </div>

        <div className={brand.g2 ?? ''}>
          <div className={ui.point ?? ''}>
            <h3 className="gb-subhead">One price</h3>
            <p className={`gb-body ${ui.pointBody ?? ''}`}>
              The price on the page is the price. Creem adds the tax for where you live and shows it
              before you pay.
            </p>
          </div>
          <div className={ui.point ?? ''}>
            <h3 className="gb-subhead">Bought once</h3>
            <p className={`gb-body ${ui.pointBody ?? ''}`}>
              No subscription and nothing to cancel. Buy a game and it stays in your library.
            </p>
          </div>
          <div className={ui.point ?? ''}>
            <h3 className="gb-subhead">A button, not a request</h3>
            <p className={`gb-body ${ui.pointBody ?? ''}`}>
              Under {REFUND_PLAYTIME_HOURS} hours played, inside {REFUND_DAYS} days: press it and
              the money goes back. Nobody has to say yes.
            </p>
          </div>
          <div className={ui.point ?? ''}>
            <h3 className="gb-subhead">Where else it is</h3>
            <p className={`gb-body ${ui.pointBody ?? ''}`}>
              Where one of our games is on another shop, its page here links there. Buy it wherever
              you like.
            </p>
          </div>
        </div>
      </Reveal>

      <Band tone="accent">
        <Eyebrow>Our mark</Eyebrow>
        <h2 className="gb-headline">
          The progress
          <br />
          strip
        </h2>
        <p className="gb-lead">Five bars. It is our mark, and it is a rule we hold ourselves to.</p>

        <figure style={{ margin: 0, ...column(3) }}>
          <VersionStrip versions={EXAMPLE_HISTORY} size="lg" onDark />
          {/*
            No colour override. `.gb-eyebrow` is the role and it carries the 70%
            ramp value the guidelines use for a label; inside this band that
            resolves to a wash of the band's own type colour rather than a fixed
            grey. An override pins it to the faint wash, which on a Solar flood
            is the least legible thing on the page.
          */}
          <figcaption className="gb-eyebrow">
            An illustration of the device. Not a real game&rsquo;s scores.
          </figcaption>
        </figure>

        <div className={brand.g2 ?? ''}>
          <div style={column(3)}>
            <h3 className="gb-subhead">What it does</h3>
            <p className="gb-body">
              Five bars for any run of steps with an end. Taller means further along, and the newest
              is on the right.
            </p>
            <p className="gb-body">
              You can watch it work: it fills as the launcher downloads and installs a game.
            </p>
          </div>
          <div style={column(3)}>
            <h3 className="gb-subhead">The one rule</h3>
            <p className="gb-body">
              We never draw a bar we do not have. If a number falls, the bar falls, and it ships
              that way.
            </p>
            <p className="gb-body">Faking it would turn the most honest thing here into a lie.</p>
          </div>
        </div>
      </Band>

      <Reveal as="section" variant="wipe" className={ui.stack}>
        <Eyebrow>Our numbers</Eyebrow>
        <h2 className="gb-headline">
          Always with
          <br />
          the count
        </h2>

        <div className={brand.g2 ?? ''}>
          <Do title="Good">
            <p className="gb-stat" style={{ fontSize: '1.15rem', lineHeight: 1.4, margin: 0 }}>
              UNDER {REFUND_PLAYTIME_HOURS} HOURS, INSIDE {REFUND_DAYS} DAYS
            </p>
            <p className="gb-body" style={{ margin: 0 }}>
              The exact test the refund button runs. You can read it before you buy.
            </p>
          </Do>
          <Never title="Not good">
            <p className="gb-subhead" style={{ fontSize: '1.15rem', lineHeight: 1.4, margin: 0 }}>
              Refunds usually accepted
            </p>
            <p className="gb-body" style={{ margin: 0 }}>
              A big word with no number behind it. You cannot check it, so it is worth nothing.
            </p>
          </Never>
        </div>
      </Reveal>

      <Rule />

      <Reveal as="section" className={ui.stack}>
        <h2 className="gb-subhead">We are not trying to be Steam</h2>
        <p className="gb-body">
          Where one of our games is on another shop, its page here links there. Buy it wherever you
          like — we would rather you played it than bought it from us.
        </p>
        <div className={brand.g3 ?? ''}>
          <Stat value="0" label="Paid ads, ever" />
          <Stat value="1" label="Studio. Every game here is ours" />
        </div>
      </Reveal>

      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        <Cta href="/">Our games</Cta>
        <Cta href="/creators">Work with us</Cta>
        <Cta href="/contact" variant="ghost">
          Say hello
        </Cta>
      </div>
    </Page>
  );
}
